import { pick } from 'ramda';
import { IBannerFeed } from 'js/components/main/Banner/types';
import { TDeviceFormFactor } from 'js/hooks/device/store/types';
import { parseCasinoJackpot, parseJackpots } from 'js/hooks/useGetJackpots/helpers';
import { IGetSlidesOptions } from 'js/components/main/Banner/hooks/useGetSlides/types';
import { parseForms } from 'js/components/common/ChangeForm/hooks/useGetForms/helpers';
import { getSliderOptions, getSlides } from 'js/components/main/Banner/hooks/useGetSlides/helpers';
import { parseGamesFeed } from 'js/hooks/games/hooks/useGetGamesFeed/helpers';
import { parseGameFeeds } from 'js/components/system/App/hooks/useGetOldGamesFeeds/helpers';
import { IGameFeedConfig } from 'js/components/system/App/store/types';
import { IGamesFeed } from 'js/hooks/games/types';
import { isIP4inCIDRs } from 'js/components/tagTypes/IPWhiteList/helpers';

const identity = <T>(value: T): T => value;

const parseJSONData = async (response: Response): Promise<
    Pick<Response, 'ok'| 'redirected'| 'status'| 'statusText'| 'type'| 'url'>| ReturnType<Response['json']>
> => {
    try {
        return await response.json();
    } catch (error: any) {
        return Promise.reject({
            error: error?.message || error,
            // to avoid DataCloneError only essential for Portal properties are taken
            // see https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm
            ...pick(['ok', 'redirected', 'status', 'statusText', 'type', 'url'], response)
        });
    }
};

const workerTypes = new Map<string, [CallableFunction, CallableFunction]>(Object.entries({
    /**
     * Generic workerTypes
     */
    json: [
        fetch,
        async (response: Response) => await parseJSONData(response)
    ],
    /**
     * Forms workerTypes
     */
    forms: [
        fetch,
        async (response: Response) => {
            const data = await parseJSONData(response);

            return parseForms(data.forms);
        }
    ],
    /**
     * Banners workerTypes
     */
    sliderOptions: [
        identity,
        async (parameters: {
            bannerFeed: IBannerFeed;
            formFactor: TDeviceFormFactor;
        }) => getSliderOptions(parameters)
    ],
    slides: [
        identity,
        async (parameters: {
            bannerFeed: IBannerFeed;
            options: IGetSlidesOptions;
        }) => getSlides(parameters)
    ],
    /**
     * Jackpot workerType
     */
    jackpot: [
        async ({ url }: {
            url: string;
        }) => fetch(url),
        async (response: Response, { currencyCode }: {
            currencyCode: string;
        }) => {
            const data = await response.json();

            if (!data.jackpots || !data.casino) {
               return Promise.reject(data);
            }

            const jackpots = parseJackpots(data.jackpots, currencyCode);
            const casinoJackpot = parseCasinoJackpot(data.casino, currencyCode);

            return {
                jackpots,
                casinoJackpot
            };
        }
    ],
    // this is for new games grid (per feed)
    gamesFeed: [
        async ({ gamesFeedURL }: {
            gamesFeedURL: string;
        }) => fetch(gamesFeedURL),
        async (response: Response, { formFactor, excludedGameCodes = [] }: {
            formFactor: TDeviceFormFactor;
            excludedGameCodes: string[];
        }) => {
            const gamesFeed = await parseJSONData(response);

            return parseGamesFeed({ gamesFeed, formFactor, excludedGameCodes });
        }
    ],
    // this is for new games grid (per feed)
    gamesFeedParse: [
        identity,
        async ({ gamesFeed, formFactor, excludedGameCodes = [] }: {
            formFactor: TDeviceFormFactor;
            excludedGameCodes: string[];
            gamesFeed: IGamesFeed;
        }) => parseGamesFeed({ gamesFeed, formFactor, excludedGameCodes })
    ],
    // this is for old games grid (all feeds at once)
    gameFeeds: [
        async ({ url }: {
            url: string;
        }) => fetch(url),
        async (response: Response, { excludedGameCodes = [] }: {
            excludedGameCodes: string[];
        }) => {
            const { gamesFeeds } = await parseJSONData(response);

            return parseGameFeeds(gamesFeeds, { excludedGameCodes });
        }
    ],
    // this is for old games grid (all feeds at once)
    gameFeedParse: [
        identity,
        async ({ oldGamesFeeds, excludedGameCodes = [] }: {
            oldGamesFeeds: IGameFeedConfig[];
            excludedGameCodes: string[];
        }) => parseGameFeeds(oldGamesFeeds, { excludedGameCodes })
    ],
    promotionsFeed: [
        async ({ url }: { url: string }) => fetch(url),
        async (response: Response) => await parseJSONData(response)
    ],
    whitelist: [
        async ({ serviceUrl }: {
            serviceUrl: string;
        }) => fetch(serviceUrl),
        async (response: Response, {
            customList,
            companyList
        }: {
            customList: { cidr: string }[];
            companyList: string;
        }) => {
            const { ip } = await parseJSONData(response);
            const isAllowed = isIP4inCIDRs(customList, companyList, ip);

            return { ip, isAllowed };
        }
    ]
}));

self.onmessage = async (event) => {
    const {
        workerType,
        correlationId,
        parameters,
        time
    } = event.data;

    try {
        const [fetcher, parser] = workerTypes.get(workerType) ?? [identity, identity];
        const response = await fetcher(parameters);
        const data = await parser(response, parameters);

        self.postMessage({
            correlationId,
            type: 'FETCH_SUCCESS',
            time: Date.now() - time,
            payload: {
                data
            }
        });
    } catch (error) {
        self.postMessage({
            correlationId,
            type: 'FETCH_FAILURE',
            time: Date.now() - time,
            payload: {
                error
            }
        });
    }
};
